/**
 * Pure playback-sync math (no hls.js dependency, so it is unit-testable in Node).
 *
 * Every viewer loads the same manifest and therefore sees the same
 * `EXT-X-PROGRAM-DATE-TIME` (PDT) values — an absolute wall-clock reference from
 * the encoder. So a common `targetPDT = liveEdgePDT − targetLatency` maps to the
 * same absolute frame on every client, regardless of when they joined or their
 * local clock. This function maps that target PDT to a media `currentTime`.
 */

export interface FragmentLike {
  /** PDT of the fragment start, ms since epoch (null if the stream has no PDT). */
  programDateTime: number | null;
  /** Media timeline position of the fragment start, seconds. */
  start: number;
  /** Fragment duration, seconds. */
  duration: number;
}

/**
 * Media `currentTime` (seconds) that corresponds to `liveEdgePDT − targetLatency`,
 * or null if the manifest has no PDT (caller falls back to hls.js latency sync).
 */
export function computeTargetTime(fragments: FragmentLike[] | undefined, targetLatencyMs: number): number | null {
  if (!fragments || fragments.length === 0) return null;
  const withPdt = fragments.filter((f) => f.programDateTime != null);
  if (withPdt.length === 0) return null;

  const last = withPdt[withPdt.length - 1]!;
  const liveEdgePdt = last.programDateTime! + last.duration * 1000; // end of the last segment
  const targetPdt = liveEdgePdt - targetLatencyMs;

  for (const f of withPdt) {
    const startPdt = f.programDateTime!;
    const endPdt = startPdt + f.duration * 1000;
    if (targetPdt >= startPdt && targetPdt < endPdt) {
      return f.start + (targetPdt - startPdt) / 1000;
    }
  }
  // Target older than the first fragment we have → oldest buffered start.
  if (targetPdt < withPdt[0]!.programDateTime!) return withPdt[0]!.start;
  // Target at/after the live edge → live edge time.
  return last.start + last.duration;
}
